import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useBypass } from '../contexts/BypassContext';
import {
  Container,
  Paper,
  Typography,
  Box,
  Avatar,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Stack,
  IconButton,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import PaywallBypassToggle from '../components/PaywallBypassToggle';
import {
  SmartToyRounded as AIIcon,
  Send as SendIcon,
  Chat as ChatIcon,
  AttachMoneyRounded as MoneyIcon,
  ScheduleRounded as ScheduleIcon,
  DirectionsCarRounded as CarIcon,
  DiamondRounded as LuxuryIcon,
  LocalActivityRounded as LocalIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  FlightTakeoff as FlightIcon,
  Hotel as HotelIcon,
  Restaurant as RestaurantIcon
} from '@mui/icons-material';
import { THEME_COLORS, THEME_GRADIENTS, THEME_RADIUS } from '../constants/theme';
import { documentPageCanvasSx } from '../constants/documentTheme';
import { usePersistentAIConversation } from '../hooks/usePersistentAIConversation';
import PremiumAIConcierge from '../components/PremiumAIConcierge';
import PaymentWall from '../components/PaymentWall';
import PreferencesPanel from '../components/PreferencesPanel';
import WhiteGloveService from '../components/WhiteGloveService';
import googleAPIService from '../services/googleAPIService';
import duffelService from '../services/duffelService';

const AIConcierge = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { bypassEnabled, isDevelopment } = useBypass();
  const [tier, setTier] = useState('free');
  const [activeStep, setActiveStep] = useState(0);
  const [message, setMessage] = useState('');
  const [preferencesOpen, setPreferencesOpen] = useState(true);
  const [showPaywall, setShowPaywall] = useState(false);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [googleDataLoading, setGoogleDataLoading] = useState(false);
  const [googleDataError, setGoogleDataError] = useState(null);
  
  const [preferences, setPreferences] = useState({
    fromLocation: '',
    toLocation: '',
    fromLocationData: null, // Google Places data for from location
    toLocationData: null,   // Google Places data for to location
    startDate: '',
    endDate: '',
    travelers: 1,
    flexibleDates: false,
    budget: '',
    budgetType: 'per_person',
    tripTypes: [],
    pace: 'balanced',
    accommodationStyle: '',
    transportPreferences: [],
    restaurantPreferences: {
      cuisineTypes: [],
      dietaryRestrictions: [],
      diningStyle: '',
      priceRange: '',
      specialRequests: ''
    },
    interests: [],
    mustSeeItems: '',
    // Google API data
    googleData: {
      flights: [],
      hotels: [],
      restaurants: [],
      attractions: []
    }
  });

  const {
    conversation,
    isLoading,
    error,
    sendMessage: sendAIMessage,
    updatePreferences: updateAIPreferences,
    clearConversation,
    isSessionExpired,
    persistedPreferences,
    savePreferences,
    isProcessing
  } = usePersistentAIConversation(preferences, tier);



  const quickActions = [
    {
      title: 'Generate Trip Suggestions',
      description: 'Get personalized recommendations',
      icon: <AIIcon />,
      color: THEME_COLORS.primary.main,
      action: () => {
        console.log('🎯 Generate Trip Suggestions clicked with preferences:', preferences);
        if (tier === 'premium') {
          // For premium users, fetch Google data first, then send AI message
          if (preferences.fromLocation && preferences.toLocation) {
            fetchGoogleData(preferences.fromLocation, preferences.toLocation, preferences.startDate, preferences.endDate);
          }
        }
        sendAIMessage('Generate trip suggestions based on my preferences');
      }
    },
    {
      title: 'Create Itinerary',
      description: 'Plan a detailed day-by-day schedule',
      icon: <ScheduleIcon />,
      color: '#4CAF50',
      action: () => sendAIMessage('Create a detailed itinerary for my trip')
    },
    {
      title: 'Travel Tips',
      description: 'Get insider tips and advice',
      icon: <ChatIcon />,
      color: '#FF9800',
      action: () => sendAIMessage('Give me travel tips for my destination')
    },
    {
      title: 'Budget Planning',
      description: 'Get cost estimates and budget advice',
      icon: <MoneyIcon />,
      color: '#9C27B0',
      action: () => sendAIMessage('Help me plan my travel budget and find cost-effective options')
    },
    {
      title: 'Local Experiences',
      description: 'Discover hidden gems and local culture',
      icon: <LocalIcon />,
      color: '#2196F3',
      action: () => sendAIMessage('Show me unique local experiences and hidden gems')
    },
    {
      title: 'Transportation',
      description: 'Get transport options and booking advice',
      icon: <CarIcon />,
      color: '#607D8B',
      action: () => sendAIMessage('Help me with transportation options and booking')
    }
  ];


  const handlePreferenceChange = (field, value) => {
    setPreferences(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayPreferenceChange = (field, value) => {
    setPreferences(prev => {
      // Handle nested restaurant preferences
      if (field.includes('restaurantPreferences.')) {
        const [parent, child] = field.split('.');
        const currentArray = prev[parent][child] || [];
        return {
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: currentArray.includes(value)
              ? currentArray.filter(item => item !== value)
              : [...currentArray, value]
          }
        };
      }
      
      // Handle regular array preferences
      const currentArray = prev[field] || [];
      return {
        ...prev,
        [field]: currentArray.includes(value)
          ? currentArray.filter(item => item !== value)
          : [...currentArray, value]
      };
    });
  };

  const handleSendMessage = () => {
    if (message.trim()) {
      sendAIMessage(message);
      setMessage('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Removed auto-save to prevent focus loss during typing
  // Preferences are now saved only when user clicks "Complete" button

  // Handle trip creation from premium AI
  const handleTripCreated = (tripData) => {
    console.log('Trip created:', tripData);
    console.log('Trip data type:', typeof tripData);
    console.log('Trip ID:', tripData?.id);
    console.log('Trip ID type:', typeof tripData?.id);
    console.log('Trip ID is NaN:', isNaN(tripData?.id));
    
    // Navigate to the specific trip details page
    if (tripData && tripData.id && !isNaN(tripData.id)) {
      console.log('Navigating to trip details:', `/trips/${tripData.id}`);
      navigate(`/trips/${tripData.id}`);
    } else {
      console.log('Invalid trip data, navigating to MyTrips');
      // Fallback to MyTrips if no trip ID
      navigate('/trips');
    }
  };

  const handlePaywallSuccess = () => {
    setTier('premium');
    setShowPaywall(false);
  };

  // Handle bypass logic - automatically set to premium when bypass is enabled
  React.useEffect(() => {
    if (isDevelopment && bypassEnabled) {
      console.log('🚫 Bypass enabled - automatically setting tier to premium');
      setTier('premium');
      setShowPaywall(false);
    }
  }, [bypassEnabled, isDevelopment]);

  // Enhanced data fetching with Duffel flights
  const fetchGoogleData = async (fromLocation, toLocation, startDate, endDate) => {
    if (!fromLocation || !toLocation) {
      console.log('❌ Missing location data:', { fromLocation, toLocation });
      return;
    }
    
    console.log('🔍 Fetching travel data for:', { fromLocation, toLocation, startDate, endDate });
    
    setGoogleDataLoading(true);
    setGoogleDataError(null);
    
    try {
      // Get airport codes from location data
      const fromAirport = preferences.fromLocationData?.iata_code || 'LHR'; // Default to London
      const toAirport = preferences.toLocationData?.iata_code || 'JFK'; // Default to New York
      
      console.log('✈️ Airport codes:', { fromAirport, toAirport });
      
      // Fetch real flights from Duffel
      const flightSearchParams = {
        origin: fromAirport,
        destination: toAirport,
        departure_date: startDate,
        return_date: endDate,
        passengers: preferences.travelers || 1,
        cabin_class: 'economy'
      };
      
      console.log('🔍 Duffel flight search params:', flightSearchParams);
      
      const duffelFlightResponse = await duffelService.searchFlights(flightSearchParams);
      const realFlights = duffelFlightResponse.flights || [];
      
      console.log('✈️ Real flights from Duffel:', realFlights.length);
      
      // Duffel hotel search disabled - returns 0 results and wastes API calls.
      const realHotels = [];
      
      // Fetch activities from Viator
      const viatorResponse = await fetch(`/api/viator/recommendations?location=${encodeURIComponent(toLocation)}&limit=10`);
      const viatorData = viatorResponse.ok ? await viatorResponse.json() : { activities: [] };
      const realActivities = viatorData.activities || [];
      
      console.log('🎯 Real activities from Viator:', realActivities.length);
      
      // Fetch other data from Google (restaurants, attractions)
      const googleData = await googleAPIService.fetchTravelData(
        fromLocation, 
        toLocation, 
        startDate, 
        endDate, 
        { ...preferences, adults: preferences.travelers || 1 }
      );
      
      // Combine all data sources
      const travelData = {
        ...googleData,
        flights: realFlights.map(flight => ({
          ...flight,
          source: 'duffel',
          bookable: true,
          booking_url: `/duffel/booking/${flight.id}`
        })),
        hotels: realHotels.map(hotel => ({
          ...hotel,
          source: 'duffel',
          bookable: true,
          booking_url: `/duffel/booking/${hotel.id}`
        })),
        activities: realActivities.map(activity => ({
          ...activity,
          source: 'viator',
          bookable: true,
          booking_url: activity.url || `https://www.viator.com/searchResults/all?text=${encodeURIComponent(toLocation)}`
        }))
      };
      
      console.log('📊 Combined travel data:', {
        flights: travelData.flights?.length || 0,
        hotels: travelData.hotels?.length || 0,
        activities: travelData.activities?.length || 0,
        restaurants: travelData.restaurants?.length || 0,
        attractions: travelData.attractions?.length || 0
      });
      
      setPreferences(prev => ({
        ...prev,
        googleData: travelData
      }));
      
    } catch (error) {
      console.error('Error fetching travel data:', error);
      
      // Fallback to Google-only data if Duffel fails
      try {
        console.log('🔄 Falling back to Google data only');
        const fallbackData = await googleAPIService.fetchTravelData(
          fromLocation, 
          toLocation, 
          startDate, 
          endDate, 
          { ...preferences, adults: preferences.travelers || 1 }
        );
        
        setPreferences(prev => ({
          ...prev,
          googleData: fallbackData
        }));
        
        setGoogleDataError('Real flight data unavailable. Using estimated prices.');
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
        setGoogleDataError('Failed to fetch travel data. Please try again.');
      }
    } finally {
      setGoogleDataLoading(false);
    }
  };

  const handleLocationSelect = (locationType, locationData) => {
    console.log('📍 Location selected:', { locationType, locationData });
    
    setPreferences(prev => {
      const newPreferences = {
        ...prev,
        [locationType]: locationData.address,
        [`${locationType}Data`]: locationData
      };
      console.log('📍 Updated preferences:', newPreferences);
      return newPreferences;
    });
  };

  const swapLocations = () => {
    setPreferences(prev => ({
      ...prev,
      fromLocation: prev.toLocation,
      toLocation: prev.fromLocation,
      fromLocationData: prev.toLocationData,
      toLocationData: prev.fromLocationData
    }));
  };

  // Handle payment success parameter
  useEffect(() => {
    const paymentSuccess = searchParams.get('payment');
    if (paymentSuccess === 'success') {
      console.log('🎉 Payment success detected - granting AI concierge access');
      setTier('premium');
      setShowPaywall(false);
      setShowPaymentSuccess(true);
      
      // Hide success message after 5 seconds
      setTimeout(() => {
        setShowPaymentSuccess(false);
      }, 5000);
    }
  }, [searchParams]);

  // Event-based preference management to prevent infinite loops
  const isInitialLoad = React.useRef(true);
  const hasRestored = React.useRef(false);

  // Save preferences when they change (event-based)
  useEffect(() => {
    // Skip on initial load to prevent immediate save
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }

    const hasSignificantChanges = preferences.toLocation || 
                                 preferences.startDate || 
                                 preferences.endDate || 
                                 preferences.fromLocation;
    
    if (hasSignificantChanges) {
      console.log('💾 Saving preferences to localStorage');
      savePreferences(preferences);
    }
  }, [preferences, savePreferences]);

  // Restore preferences from localStorage on load (event-based, only once)
  useEffect(() => {
    if (Object.keys(persistedPreferences).length > 0 && !hasRestored.current) {
      console.log('🔄 Restoring preferences from localStorage');
      hasRestored.current = true;
      setPreferences(prev => ({ ...prev, ...persistedPreferences }));
    }
  }, [persistedPreferences]);

  // Update step when AI processing completes or when returning with results
  useEffect(() => {
    if (!isProcessing && activeStep === 0) {
      // Check if we have results in the conversation
      const hasResults = conversation.some(msg => 
        msg.type === 'ai' && 
        msg.data && 
        (msg.data.locations || msg.data.hotels || msg.data.restaurants || msg.data.activities)
      );
      
      if (hasResults) {
        console.log('🎉 AI processing completed with results, moving to step 1');
        setActiveStep(1);
      }
    }
  }, [isProcessing, activeStep, conversation]);

  // Check for existing results on component mount
  useEffect(() => {
    if (conversation.length > 0) {
      const hasResults = conversation.some(msg => 
        msg.type === 'ai' && 
        msg.data && 
        (msg.data.locations || msg.data.hotels || msg.data.restaurants || msg.data.activities)
      );
      
      if (hasResults && activeStep === 0) {
        console.log('🔄 Found existing results, moving to step 1');
        setActiveStep(1);
      }
    }
  }, [conversation, activeStep]);

  // Clear conversation when preferences change significantly - DISABLED
  // This was causing AI responses to be cleared immediately after being added
  // Users can manually clear conversation using the "Start Over" button if needed
  /*
  useEffect(() => {
    const hasSignificantChanges = preferences.toLocation || 
                                 preferences.startDate || 
                                 preferences.endDate || 
                                 preferences.fromLocation;
    
    // Only clear if there's significant changes, an actual conversation, AND we're not currently loading
    if (hasSignificantChanges && conversation.length > 1 && !isLoading) {
      // Only clear if there's an actual conversation (more than just the initial message)
      console.log('🔄 Significant preference changes detected - clearing conversation');
      clearConversation();
    }
  }, [preferences.toLocation, preferences.startDate, preferences.endDate, preferences.fromLocation, conversation.length, isLoading, clearConversation]);
  */

  // Removed duplicate PreferencesPanel - using imported component instead

  return (
    <Box sx={documentPageCanvasSx}>
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Payment Success Message */}
      {showPaymentSuccess && (
        <Alert 
          severity="success" 
          sx={{ 
            mb: 3,
            borderRadius: THEME_RADIUS.small,
            '& .MuiAlert-message': {
              fontSize: '1rem',
              fontWeight: 500
            }
          }}
        >
          🎉 Payment successful! You now have access to the AI Concierge premium features.
        </Alert>
      )}

      {/* Session Expiration Warning */}
      {isSessionExpired && (
        <Alert 
          severity="warning" 
          sx={{ 
            mb: 3,
            borderRadius: THEME_RADIUS.small,
            '& .MuiAlert-message': {
              fontSize: '1rem',
              fontWeight: 500
            }
          }}
        >
          ⏰ Your session has expired. Starting a fresh conversation.
        </Alert>
      )}

      {/* Processing State Warning */}
      {isProcessing && (
        <Alert 
          severity="info" 
          sx={{ 
            mb: 3,
            borderRadius: THEME_RADIUS.small,
            '& .MuiAlert-message': {
              fontSize: '1rem',
              fontWeight: 500
            }
          }}
        >
          🤖 AI is generating your recommendations... You can navigate away and return to see the results.
        </Alert>
      )}
      
      {/* Header Section */}
      <Paper 
        elevation={0}
        sx={{ 
          background: THEME_GRADIENTS.primary,
          color: 'white',
          borderRadius: THEME_RADIUS.small,
          mb: { xs: 3, md: 4 },
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ p: { xs: 2, md: 5 } }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: { xs: 2, md: 3 },
            flexDirection: { xs: 'column', sm: 'row' },
            textAlign: { xs: 'center', sm: 'left' }
          }}>
            <Avatar
              sx={{
                width: { xs: 50, md: 60 },
                height: { xs: 50, md: 60 },
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                mr: { xs: 0, sm: 3 },
                mb: { xs: 2, sm: 0 },
                border: '2px solid rgba(255, 255, 255, 0.3)',
              }}
            >
              <AIIcon sx={{ fontSize: { xs: 24, md: 32 }, color: 'white' }} />
            </Avatar>
            <Box>
              <Typography variant="h4" sx={{ 
                fontWeight: 700, 
                color: 'white', 
                mb: 1,
                fontSize: { xs: '1.5rem', md: '2rem' }
              }}>
                AI Trip Concierge
              </Typography>
              <Typography variant="h6" sx={{ 
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: { xs: '1rem', md: '1.25rem' }
              }}>
                Your personal travel assistant powered by AI
              </Typography>
            </Box>
          </Box>
          
          <Typography variant="body1" sx={{ 
            color: 'rgba(255, 255, 255, 0.8)', 
            maxWidth: '600px', 
            mb: 2,
            fontSize: { xs: '0.9rem', md: '1rem' },
            textAlign: { xs: 'center', sm: 'left' }
          }}>
            Get personalized travel recommendations, plan itineraries, find the best deals, 
            and discover hidden gems with our intelligent AI assistant.
          </Typography>
          
          {/* Pricing Note */}
          <Box sx={{ 
            bgcolor: 'rgba(255, 255, 255, 0.1)', 
            p: { xs: 1.5, md: 2 }, 
            borderRadius: 2, 
            mb: { xs: 2, md: 3 },
            maxWidth: '600px',
            mx: { xs: 'auto', sm: 0 }
          }}>
            <Typography variant="body2" sx={{ 
              color: 'rgba(255, 255, 255, 0.9)', 
              fontWeight: 500, 
              mb: 1,
              fontSize: { xs: '0.8rem', md: '0.875rem' },
              textAlign: { xs: 'center', sm: 'left' }
            }}>
              💳 Premium Access: $35 per trip or daily renewal
            </Typography>
            <Typography variant="body2" sx={{ 
              color: 'rgba(255, 255, 255, 0.7)', 
              fontSize: { xs: '0.75rem', md: '0.9rem' },
              textAlign: { xs: 'center', sm: 'left' }
            }}>
              Access expires after 1 day OR when you convert recommendations to a real trip. 
              New payment required for each new trip or daily renewal.
            </Typography>
          </Box>
          
          {/* Tier Selection */}
          <Box sx={{ 
            display: 'flex', 
            gap: { xs: 1, md: 2 }, 
            flexWrap: 'wrap',
            justifyContent: { xs: 'center', sm: 'flex-start' }
          }}>
            <Button
              variant={tier === 'free' ? 'contained' : 'outlined'}
              onClick={() => setTier('free')}
              sx={{
                px: { xs: 2, md: 3 },
                py: { xs: 1, md: 1.5 },
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: { xs: '0.8rem', md: '0.875rem' },
                backgroundColor: tier === 'free' ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                borderColor: 'rgba(255, 255, 255, 0.5)',
                color: 'white',
                minWidth: { xs: '80px', md: 'auto' },
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderColor: 'rgba(255, 255, 255, 0.7)',
                }
              }}
            >
              Free Tier
            </Button>
            <Button
              variant={tier === 'premium' ? 'contained' : 'outlined'}
              onClick={() => {
                console.log('🎯 Premium Tier button clicked');
                // Just set tier to premium - paywall will show when user clicks "Generate My Trip"
                setTier('premium');
                setShowPaywall(false);
              }}
              startIcon={<LuxuryIcon sx={{ fontSize: { xs: 16, md: 20 } }} />}
              sx={{
                px: { xs: 2, md: 3 },
                py: { xs: 1, md: 1.5 },
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: { xs: '0.8rem', md: '0.875rem' },
                backgroundColor: tier === 'premium' ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                borderColor: 'rgba(255, 255, 255, 0.5)',
                color: 'white',
                minWidth: { xs: '100px', md: 'auto' },
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderColor: 'rgba(255, 255, 255, 0.7)',
                }
              }}
            >
              Premium Tier
            </Button>
            <Button
              variant={tier === 'white-glove' ? 'contained' : 'outlined'}
              onClick={() => {
                setTier('white-glove');
              }}
              startIcon={<LuxuryIcon sx={{ fontSize: { xs: 16, md: 20 } }} />}
              sx={{
                px: { xs: 2, md: 3 },
                py: { xs: 1, md: 1.5 },
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: { xs: '0.8rem', md: '0.875rem' },
                backgroundColor: tier === 'white-glove' ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                borderColor: 'rgba(255, 255, 255, 0.5)',
                color: 'white',
                minWidth: { xs: '120px', md: 'auto' },
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderColor: 'rgba(255, 255, 255, 0.7)',
                }
              }}
            >
              White Glove Service
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Tip for Free and Premium Users */}
      {tier !== 'white-glove' && (
        <Alert severity="info" sx={{ mb: 3 }}>
          💡 <strong>Tip:</strong> Book your flights and hotels as a package, then create a trip and add all the details to your itinerary for easy planning!
          <Typography variant="body2" sx={{ mt: 1, fontSize: '0.875rem' }}>
            Only White Glove Service includes full trip creation and booking assistance.
          </Typography>
        </Alert>
      )}

      {/* Common Preferences Panel */}
      <Accordion 
        expanded={preferencesOpen} 
        onChange={() => setPreferencesOpen(!preferencesOpen)}
        sx={{ 
          mb: 3,
          '&:before': { display: 'none' },
          boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
          borderRadius: 2,
          '& .MuiAccordionSummary-root': {
            minHeight: 56, // Ensure minimum height
            '&.Mui-expanded': {
              minHeight: 56
            }
          }
        }}
      >
        <AccordionSummary
          expandIcon={preferencesOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{
            backgroundColor: preferencesOpen ? THEME_GRADIENTS.primary : THEME_COLORS.primary.dark,
            color: 'white',
            borderRadius: preferencesOpen ? '8px 8px 0 0' : '8px',
            '&:hover': {
              backgroundColor: THEME_COLORS.primary.dark,
            },
            '& .MuiAccordionSummary-content': {
              alignItems: 'center',
              gap: 2,
              margin: '12px 0 !important' // Ensure content is always visible
            }
          }}
        >
          <AIIcon sx={{ fontSize: 24 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Trip Preferences
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, ml: 'auto' }}>
            {preferencesOpen ? 'Click to collapse' : 'Click to expand'}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <PreferencesPanel 
            preferences={preferences}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            handlePreferenceChange={handlePreferenceChange}
            handleArrayPreferenceChange={handleArrayPreferenceChange}
            handleLocationSelect={handleLocationSelect}
            swapLocations={swapLocations}
            handleSavePreferences={() => {
              updateAIPreferences(preferences);
              sendAIMessage("I've completed setting up my trip preferences. Please help me plan my trip based on these details.");
            }}
          />
        </AccordionDetails>
      </Accordion>

      {/* Google API Data Display - Only show for premium users when data has been fetched */}
      {tier === 'premium' && preferences.googleData && (preferences.googleData.flights.length > 0 || preferences.googleData.hotels.length > 0 || preferences.googleData.restaurants.length > 0) && (
        <Accordion defaultExpanded={false} sx={{ mb: 3 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Make your Bookings
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
              <Grid container spacing={3}>
                {/* Flights */}
                {preferences.googleData.flights.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <FlightIcon sx={{ mr: 1, color: THEME_COLORS.primary }} />
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Flights
                          </Typography>
                        </Box>
                        {preferences.googleData.flights.map((flight, index) => (
                          <Box key={index} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, mb: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {flight.airline}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {flight.departure} → {flight.arrival} ({flight.duration})
                            </Typography>
                            <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                              {flight.price}
                            </Typography>
                          </Box>
                        ))}
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Hotels */}
                {preferences.googleData.hotels.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <HotelIcon sx={{ mr: 1, color: THEME_COLORS.primary }} />
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Hotels
                          </Typography>
                        </Box>
                        {preferences.googleData.hotels.map((hotel, index) => (
                          <Box key={index} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, mb: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {hotel.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {hotel.location} • ⭐ {hotel.rating}
                            </Typography>
                            <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                              {hotel.price}
                            </Typography>
                          </Box>
                        ))}
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Restaurants */}
                {preferences.googleData.restaurants.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <RestaurantIcon sx={{ mr: 1, color: THEME_COLORS.primary }} />
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Restaurants
                          </Typography>
                        </Box>
                        {preferences.googleData.restaurants.map((restaurant, index) => (
                          <Box key={index} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, mb: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {restaurant.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {restaurant.cuisine} • {restaurant.priceRange} • ⭐ {restaurant.rating}
                            </Typography>
                          </Box>
                        ))}
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Attractions */}
                {preferences.googleData.attractions.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <LocalIcon sx={{ mr: 1, color: THEME_COLORS.primary }} />
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Attractions
                          </Typography>
                        </Box>
                        {preferences.googleData.attractions.map((attraction, index) => (
                          <Box key={index} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, mb: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {attraction.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {attraction.type} • ⭐ {attraction.rating} • {attraction.price}
                            </Typography>
                          </Box>
                        ))}
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            
            {googleDataLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ ml: 2 }}>
                  Loading travel options...
                </Typography>
              </Box>
            )}
            
            {!googleDataLoading && preferences.googleData && 
             preferences.googleData.flights.length === 0 && 
             preferences.googleData.hotels.length === 0 && 
             preferences.googleData.restaurants.length === 0 && 
             preferences.googleData.attractions.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No travel options found for your selected locations.
                </Typography>
              </Box>
            )}
            
            {googleDataError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {googleDataError}
              </Alert>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Tier-Specific Content */}
      {tier === 'premium' ? (
        <PremiumAIConcierge preferences={preferences} onTripCreated={handleTripCreated} />
      ) : tier === 'white-glove' ? (
        <WhiteGloveService 
          preferences={preferences} 
          onClose={() => {
            setTier('free');
          }} 
        />
      ) : (
        <Grid container spacing={{ xs: 2, md: 4 }}>
          {/* Chat Interface */}
          <Grid item xs={12} md={8}>
            <Card sx={{ 
              height: { xs: '500px', md: '600px' }, 
              display: 'flex', 
              flexDirection: 'column' 
            }}>
              <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 0 }}>
                {/* Error Display */}
                {error && (
                  <Box sx={{ p: 2 }}>
                    <Alert severity="error">
                      {error}
                    </Alert>
                  </Box>
                )}

                {/* Chat Header with Clear Button */}
                {conversation.length > 1 && (
                  <Box sx={{ 
                    p: 2, 
                    borderBottom: '1px solid', 
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                      Chat with AI Concierge
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        clearConversation();
                        // Also clear Travel Options data
                        setPreferences(prev => ({
                          ...prev,
                          googleData: {
                            flights: [],
                            hotels: [],
                            restaurants: [],
                            attractions: []
                          }
                        }));
                        setGoogleDataLoading(false);
                        setGoogleDataError(null);
                      }}
                      sx={{ 
                        minWidth: 'auto',
                        px: 2,
                        py: 0.5,
                        fontSize: '0.75rem'
                      }}
                    >
                      Clear Chat
                    </Button>
                  </Box>
                )}

                {/* Chat Messages */}
                <Box sx={{ 
                  flex: 1, 
                  p: { xs: 2, md: 3 }, 
                  overflowY: 'auto', 
                  maxHeight: { xs: '350px', md: '450px' } 
                }}>
                  {conversation.length === 0 && (
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center', 
                      height: '100%',
                      flexDirection: 'column',
                      textAlign: 'center'
                    }}>
                      <AIIcon sx={{ fontSize: 48, color: THEME_COLORS.primary.main, mb: 2 }} />
                      <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                        Welcome to AI Trip Concierge!
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Ask me anything about your trip or use the quick actions to get started.
                      </Typography>
                    </Box>
                  )}
                  {conversation.map((msg) => (
                    <Box
                      key={msg.id}
                      sx={{
                        display: 'flex',
                        justifyContent: msg.type === 'user' ? 'flex-end' : 'flex-start',
                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{
                          maxWidth: '70%',
                          p: 2,
                          borderRadius: 2,
                          backgroundColor: msg.type === 'user' 
                            ? THEME_COLORS.primary.main
                            : msg.isError 
                              ? 'rgba(244, 67, 54, 0.1)'
                              : msg.isSuggestion || msg.isItinerary || msg.isTips
                                ? 'rgba(76, 175, 80, 0.1)'
                                : 'rgba(0, 0, 0, 0.05)',
                          color: msg.type === 'user' ? 'white' : 'text.primary',
                          border: msg.isError ? '1px solid rgba(244, 67, 54, 0.3)' : 'none',
                        }}
                      >
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                          {msg.message}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: msg.type === 'user' 
                              ? 'rgba(255, 255, 255, 0.7)' 
                              : 'text.secondary',
                            fontSize: '0.75rem',
                            mt: 0.5,
                            display: 'block'
                          }}
                        >
                          {msg.timestamp.toLocaleTimeString()}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                  
                  {/* Loading indicator */}
                  {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          backgroundColor: 'rgba(0, 0, 0, 0.05)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <CircularProgress size={16} />
                        <Typography variant="body2" color="text.secondary">
                          AI is thinking...
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>

                {/* Message Input */}
                <Box sx={{ 
                  p: { xs: 2, md: 3 }, 
                  borderTop: '1px solid', 
                  borderColor: 'divider' 
                }}>
                  <Stack direction="row" spacing={{ xs: 1, md: 2 }} alignItems="flex-end">
                    <TextField
                      fullWidth
                      multiline
                      maxRows={3}
                      placeholder="Ask me anything about your trip..."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: 'white',
                          fontSize: { xs: '0.9rem', md: '1rem' },
                          '& fieldset': {
                            borderColor: '#e0e0e0',
                          },
                          '&:hover fieldset': {
                            borderColor: THEME_COLORS.primary.main,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: THEME_COLORS.primary.main,
                            borderWidth: 2,
                          },
                        },
                        '& .MuiInputBase-input': {
                          color: '#333',
                          fontSize: { xs: '0.9rem', md: '1rem' },
                          '&::placeholder': {
                            color: '#999',
                            opacity: 1,
                          }
                        }
                      }}
                    />
                    <IconButton
                      onClick={handleSendMessage}
                      disabled={!message.trim() || isLoading}
                      sx={{
                        backgroundColor: message.trim() ? THEME_COLORS.primary.main : '#e0e0e0',
                        color: message.trim() ? 'white' : '#999',
                        minWidth: { xs: 40, md: 48 },
                        minHeight: { xs: 40, md: 48 },
                        borderRadius: 2,
                        '&:hover': {
                          backgroundColor: message.trim() ? THEME_COLORS.primary.dark : '#d0d0d0',
                        },
                        '&:disabled': {
                          backgroundColor: '#e0e0e0',
                          color: '#999',
                        }
                      }}
                    >
                      {isLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon sx={{ fontSize: { xs: 18, md: 20 } }} />}
                    </IconButton>
                  </Stack>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Quick Actions */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Typography variant="h6" sx={{ 
                  mb: { xs: 2, md: 3 }, 
                  fontWeight: 600,
                  fontSize: { xs: '1.1rem', md: '1.25rem' }
                }}>
                  Quick Actions
                </Typography>
                <Grid container spacing={{ xs: 1, md: 2 }}>
                  {quickActions.map((action, index) => (
                    <Grid item xs={12} sm={6} md={12} key={index}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={action.icon}
                        onClick={action.action}
                        disabled={isLoading}
                        sx={{
                          justifyContent: 'flex-start',
                          p: { xs: 1.5, md: 2 },
                          textAlign: 'left',
                          borderColor: action.color,
                          color: action.color,
                          fontSize: { xs: '0.8rem', md: '0.875rem' },
                          minHeight: { xs: 40, md: 48 },
                          '&:hover': {
                            backgroundColor: `${action.color}10`,
                            borderColor: action.color,
                          },
                          '&:disabled': {
                            opacity: 0.6,
                          }
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {action.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {action.description}
                          </Typography>
                        </Box>
                      </Button>
                    </Grid>
                  ))}
                </Grid>
                
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* AI Concierge Paywall */}
      <PaymentWall
        open={showPaywall}
        onClose={() => setShowPaywall(false)}
        tripId={null}
        tripTitle="AI Concierge Service"
        isOwner={true}
        feature="AI Concierge"
        tierType="ai_concierge"
        onSuccess={handlePaywallSuccess}
      />

      
      {/* Development Paywall Bypass Toggle */}
      <PaywallBypassToggle />
    </Container>
    </Box>
  );
};

export default AIConcierge;